import { put, takeLatest, takeEvery, call, take, select, all } from 'redux-saga/effects'
import { SubmissionError, setSubmitSucceeded, setSubmitFailed, submit } from 'redux-form'

import { errorHandler } from '../../../core/helpers/errorHelpers'
import {
  createJobExperienceSuccess,
  createJobExperienceError,
  fetchJobExperiencesSuccess,
  fetchJobExperiencesError,
  updateJobExperienceSuccess,
  updateJobExperienceError,
  deleteJobExperienceSuccess,
  deleteJobExperienceError,
  CREATE,
  FETCH,
  UPDATE,
  DELETE,
  SUBMIT,
} from '../modules/jobExperiences'
import { updateCandidate } from '../modules/candidate'
import { currentlyEditingFormNames, hasSubmittedJobExperiences } from '../selectors/areaRoles'
import { getLocation } from '../../selectors/router'
import { nextOnboardingUrl } from '../../helpers/onboardingSteps'
import { createJobExperience, fetchJobExperiences, updateJobExperience, deleteJobExperience } from '../api'

const SET_SUBMIT_SUCCEEDED = setSubmitSucceeded().type
const SET_SUBMIT_FAILED = setSubmitFailed().type

function* createJobExperienceSaga({ meta }) {
  try {
    const response = yield createJobExperience(meta.candidateId, meta.data)
    yield call(meta.resolve)
    yield put(createJobExperienceSuccess(response))
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(createJobExperienceError(error))
    yield call(meta.reject, new SubmissionError({ _error: error }))
  }
}

function* fetchJobExperiencesSaga({ meta }) {
  try {
    const response = yield fetchJobExperiences(meta.id)
    yield put(fetchJobExperiencesSuccess(response))
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(fetchJobExperiencesError(error))
  }
}

function* updateJobExperienceSaga({ meta }) {
  try {
    const response = yield updateJobExperience(meta.data)
    yield call(meta.resolve)
    yield put(updateJobExperienceSuccess(response))
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(updateJobExperienceError(error))
    yield call(meta.reject, new SubmissionError({ _error: error }))
  }
}

function* deleteJobExperienceSaga({ meta }) {
  try {
    // Fake successful deletion
    // TODO: Handle error if it does occur
    yield put(deleteJobExperienceSuccess(meta.id))
    yield deleteJobExperience(meta.id)
  } catch (err) {
    const { error } = yield call(errorHandler, err)
    yield put(deleteJobExperienceError(error))
  }
}

function* submitRoleForm(formName) {
  // Submit the form
  yield put(submit(formName))

  // Resolve upon either successful submission or failure of the form
  yield take(action => {
    return (action.type === SET_SUBMIT_SUCCEEDED || action.type === SET_SUBMIT_FAILED) && action.meta.form === formName
  })
}

function* submitJobExperienceSaga({ meta, payload }) {
  try {
    // Submit job experience forms that are currently being edited
    // Await successful submission or failure of each form
    const formNames = yield select(currentlyEditingFormNames)
    yield all(formNames.map(formName => call(submitRoleForm, formName)))

    const hasRoles = yield select(hasSubmittedJobExperiences)

    // Ensure that at least one role exists and submit section
    // Otherwise raise error
    if (!hasRoles) {
      yield call(
        meta.reject,
        new SubmissionError({
          _error: 'Please add at least one role before continuing.',
        }),
      )
    } else {
      const location = yield select(getLocation)
      const url = nextOnboardingUrl(location.pathname)
      yield put(updateCandidate(payload.data, url))
      yield call(meta.resolve)
    }
  } catch (err) {
    yield call(errorHandler, err)
  }
}

export default function* watchJobExperiencesSaga() {
  yield takeEvery(CREATE, createJobExperienceSaga)
  yield takeLatest(FETCH, fetchJobExperiencesSaga)
  yield takeEvery(UPDATE, updateJobExperienceSaga)
  yield takeLatest(DELETE, deleteJobExperienceSaga)
  yield takeLatest(SUBMIT, submitJobExperienceSaga)
}
